/* ----------------------------------------------------------------------------
   Update Rates & TAT wizard — pure computation helpers (React port of the
   data helpers inside m0.js renderUpdate). No React, no DOM — these operate on
   the wizard state object {partnerId, codes, scope, rates, tat, tatBase, ...}.
   ---------------------------------------------------------------------------- */
import {
  DUMMY_CODES,
  DUMMY_RATECARDS,
  RATECARD_HISTORY,
  TAT_ZONES,
  proshipRow,
  pricingCsv,
  agreementById,
  TODAY,
} from '../dummyData'

export {TAT_ZONES}

/* ── Standard TATs by service type (Zone A–E → local/regional/metro/roi/special).
   Source: "Merchant CP Wise Rates Setup Base Structure" sheet (mockup STD_TAT).
   Used to PRE-FILL the TAT update grid as editable defaults. ── */
const STD_TAT_ZK = ['local', 'regional', 'metro', 'roi', 'special']
const STD_TAT = {
  FWD_SURFACE_REG: [[1, 2], [2, 3], [3, 5], [5, 7], [7, 9]],
  FWD_SURFACE_LARGE: [[1, 2], [4, 5], [4, 5], [5, 6], [7, 9]],
  FWD_AIR_REG: [[1, 2], [2, 3], [2, 3], [3, 4], [6, 7]],
  FWD_AIR_LARGE: [[1, 2], [2, 3], [2, 3], [3, 4], [6, 7]],
  HV_FWD_REG: [[1, 2], [2, 3], [3, 5], [5, 7], [7, 9]],
  HV_FWD_LARGE: [[1, 2], [4, 5], [4, 5], [5, 6], [7, 9]],
  HV_REVERSE_REG: [[5, 6], [8, 9], [8, 9], [9, 10], [11, 13]],
  HV_REVERSE_LARGE: [[5, 6], [8, 9], [8, 9], [9, 10], [11, 13]],
  SDD_FWD_REG: [[0, 1], [0, 1], [0, 1], [1, 2], [1, 2]],
  SDD_FWD_LARGE: [[0, 1], [0, 1], [0, 1], [1, 2], [1, 2]],
  SC_NDD_FWD_REG: [[1, 2], [1, 2], [1, 2], [1, 2], [1, 2]],
  SC_NDD_FWD_LARGE: [[1, 2], [1, 2], [1, 2], [1, 2], [1, 2]],
  INTERCITY_NDD_REG: [[1, 2], [1, 2], [1, 2], [1, 2], [1, 2]],
  INTERCITY_NDD_LARGE: [[1, 2], [1, 2], [1, 2], [1, 2], [1, 2]],
  H2TO4: [[0, 1], [0, 1], [0, 1], [0, 1], [0, 1]],
  SUB2H: [[0, 1], [0, 1], [0, 1], [0, 1], [0, 1]],
  REV_REG_NOQC: [[5, 6], [8, 9], [8, 9], [9, 10], [11, 13]],
  REV_LARGE_NOQC: [[5, 6], [8, 9], [8, 9], [9, 10], [11, 13]],
  REV_REG_QC: [[5, 6], [6, 7], [8, 9], [9, 10], [11, 12]],
  REV_LARGE_QC: [[5, 6], [6, 7], [8, 9], [9, 10], [11, 12]],
}
function stdTatFor(code) {
  if (STD_TAT[code]) return STD_TAT[code]
  const c = DUMMY_CODES.find((x) => x.code === code)
  if (c) {
    const sts = c.serviceTypes || c.svc || []
    for (let i = 0; i < sts.length; i++) {
      if (STD_TAT[sts[i]]) return STD_TAT[sts[i]]
    }
  }
  return STD_TAT.FWD_SURFACE_REG
}
/* standard min/max (strings) for a code × zone — mockup stdCell() */
export function stdCell(code, zkey) {
  const r = stdTatFor(code)
  const i = STD_TAT_ZK.indexOf(zkey)
  const p = r && r[i]
  return p ? {min: String(p[0]), max: String(p[1])} : {min: '', max: ''}
}

export const todayIso = () => TODAY.toISOString().slice(0, 10)
export const defaultEff = () => new Date(TODAY.getTime() + 86400000).toISOString().slice(0, 10)

export const codeObj = (code) => DUMMY_CODES.find((c) => c.code === code)
export const codeVertical = (code) => {
  const c = codeObj(code)
  return c ? c.vertical : 'D2C'
}

/* current rate-card version for a code (live rows first, else history head) */
export function curVer(code) {
  const rows = DUMMY_RATECARDS.filter((r) => r.code === code)
  if (rows.length) return rows[0].version
  const h = (RATECARD_HISTORY[code] || [])[0]
  return h ? h.version : 0
}

export const agrShort = (id) => {
  const a = agreementById(id)
  if (!a) return id
  return a.type === 'Commercial Addendum' ? 'Addendum #' + a.addendumNo : a.type
}

/* Build the full ProShip matrix (5 zones × 12 weight slabs). Overlay current
   prices where the code has an existing card (matched by zone); brand-new codes
   start with basePrice null (= NEW). Returns {rows:[...]}. */
export function buildSeededRates(code) {
  const cur = DUMMY_RATECARDS.filter((r) => r.code === code)
  const hasCur = cur.length > 0
  const curByZone = {}
  cur.forEach((r) => {
    const zn = parseInt(String(r.zone).replace(/[^0-9]/g, ''), 10) || 0
    if (zn && curByZone[zn] == null) curByZone[zn] = r.rate
  })
  const ptl = codeVertical(code) === 'PTL'
  const rows = []
  for (let z = 1; z <= 5; z++) {
    for (let i = 0; i < 12; i++) {
      const lo = i * 500
      const hi = (i + 1) * 500
      const seeded = Math.round((ptl ? 180 : 24) + (z - 1) * (ptl ? 40 : 8) + i * (ptl ? 22 : 6))
      let price
      let base
      if (hasCur) {
        base = i === 0 && curByZone[z] != null ? curByZone[z] : seeded
        price = base
      } else {
        price = seeded
        base = null
      }
      rows.push(
        proshipRow(z, lo, hi, price, base, {
          rvpNoQc: 84 + (z - 1) * 10 + (i % 3) * 22,
          rvpQc: 149 + (z - 1) * 10 + (i % 3) * 22,
        })
      )
    }
  }
  return {rows}
}

/* standard TAT from the CP-wise rates sheet prefilled per zone (editable) →
   {tat:{zoneKey:{min,max}}, base:{...}} (strings). Mirrors mockup seedTat:
   every zone is prefilled with stdCell values and the baseline is the same. */
export function buildSeededTat(code) {
  const t = {}
  const base = {}
  TAT_ZONES.forEach((z) => {
    const sc = stdCell(code, z.key)
    t[z.key] = {min: sc.min, max: sc.max}
    base[z.key] = {min: sc.min, max: sc.max}
  })
  return {tat: t, base}
}

/* rows whose price differs from baseline (or is brand-new) */
export function rateChangedRows(U, code) {
  const e = U.rates[code]
  if (!e || !U.scope.rates) return []
  return e.rows.filter((r) => r.basePrice == null || (r.price !== '' && +r.price !== +r.basePrice))
}

/* zones whose min/max differs from baseline */
export function tatChangedZones(U, code) {
  const t = U.tat[code]
  if (!t || !U.scope.tat) return []
  return TAT_ZONES.filter((z) => {
    const v = t[z.key] || {min: '', max: ''}
    const bse = (U.tatBase[code] || {})[z.key]
    if (!bse) return v.min !== '' || v.max !== ''
    return String(v.min) !== String(bse.min) || String(v.max) !== String(bse.max)
  })
}

export function totals(U) {
  let rate = 0
  let tat = 0
  const pcts = []
  U.codes.forEach((code) => {
    const rch = rateChangedRows(U, code)
    rate += rch.length
    rch.forEach((r) => {
      if (r.basePrice != null && +r.basePrice > 0 && parseFloat(r.price) > 0)
        pcts.push(((+r.price - r.basePrice) / r.basePrice) * 100)
    })
    tat += tatChangedZones(U, code).length
  })
  let avg = null
  if (pcts.length) avg = Math.round((pcts.reduce((a, b) => a + b, 0) / pcts.length) * 10) / 10
  return {rate, tat, avg}
}

/* delta pill descriptor for a current→new price (NEW / No change / ±%) */
export function deltaPill(o, n) {
  if (o == null) return {cls: 'mp-green', label: 'NEW'}
  const nv = parseFloat(n)
  if (!(nv > 0)) return {cls: 'mp-red', label: 'Invalid'}
  if (nv === +o) return {cls: 'mp-grey', label: 'No change'}
  const pct = Math.round(((nv - o) / o) * 1000) / 10
  return nv > o
    ? {cls: 'mp-red', label: '▲ +' + pct + '%'}
    : {cls: 'mp-green', label: '▼ ' + pct + '%'}
}

/* functional mock of a CSV parse — a simulated revision over current prices */
export function sampleRevisionRows(rows) {
  return rows.map((r, i) => {
    const bp = r.basePrice != null ? r.basePrice : r.price
    const pct = [1.08, 0.96, 1.12][i % 3]
    return {...r, price: Math.max(1, Math.round(bp * pct))}
  })
}

/* download the CURRENT rate card in the exact ProShip upload format (price=current) */
export function buildTemplate(U) {
  let rows = []
  U.codes.forEach((code) => {
    if (U.rates[code]) rows = rows.concat(U.rates[code].rows)
  })
  return pricingCsv(rows, true)
}

/* generated rate CSV (price = new) for the review/commit bundle */
export function genRateCsv(U) {
  let rows = []
  U.codes.forEach((code) => {
    rows = rows.concat(U.rates[code] ? U.rates[code].rows : [])
  })
  return pricingCsv(rows)
}

/* generated TAT CSV for the review/commit bundle */
export function genTatCsv(U) {
  const lines = ['courier_code,zone,min_days,max_days,effective_from']
  U.codes.forEach((code) => {
    TAT_ZONES.forEach((z) => {
      const v = (U.tat[code] || {})[z.key]
      if (v && v.min !== '' && v.min != null)
        lines.push([code, z.name, v.min, v.max, U.effectiveFrom].join(','))
    })
  })
  return lines.join('\n')
}

/* TAT scale band geometry (matches mockup band() with SCALE=7) */
export function tatBand(v) {
  const SCALE = 7
  const mn = parseFloat(v.min)
  const mx = parseFloat(v.max)
  if (isNaN(mn) || isNaN(mx) || mn > mx) return {l: 0, w: 0}
  const l = Math.max(0, Math.min(1, (mn - 1) / (SCALE - 1)))
  const r = Math.max(0, Math.min(1, (mx - 1) / (SCALE - 1)))
  return {l: l * 100, w: Math.max(4, (r - l) * 100)}
}
